<script setup>
import { Menu, MenuButton, MenuItems } from '@headlessui/vue'
import { ref, computed } from 'vue'

const props = defineProps({
  placement: { type: String, default: 'bottom-end' },
  // 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  width: { type: String, default: 'w-44' },
  // flush: true removes inner py-1 and uses design-system border+shadow (for panels where items define their own padding)
  flush: { type: Boolean, default: false },
  /**
   * Teleport the panel to <body> and position it `fixed` from the trigger's
   * rect (zIndex 10000). Use when the trigger sits inside a clipping/stacking
   * context — a scroll container (`overflow-y-auto`), a `sticky`/`z-*` header,
   * a transformed ancestor — that would otherwise cut the panel off. Mirrors
   * AppMenuPanel's `portal`.
   */
  portal: { type: Boolean, default: false },
})

const rootRef = ref(null)
const portalStyle = ref({})

const placementClass = {
  'bottom-end':   'top-full right-0 mt-1 origin-top-right',
  'bottom-start': 'top-full left-0 mt-1 origin-top-left',
  'top-end':      'bottom-full right-0 mb-1 origin-bottom-right',
  'top-start':    'bottom-full left-0 mb-1 origin-bottom-left',
}

const baseClass = computed(() => props.flush
  ? 'bg-white rounded-lg border border-gray-200 shadow-[0_2px_10px_rgba(9,38,67,0.12)] overflow-hidden focus:outline-none'
  : 'bg-white rounded-lg shadow-lg ring-1 ring-gray-200 py-1 focus:outline-none')

const panelClass = computed(() => {
  if (props.portal) { 
    return `fixed z-[10000] ${baseClass.value} ${props.width}`
  }

  const place = placementClass[props.placement] ?? placementClass['bottom-end']

  return `absolute z-[9999] ${baseClass.value} ${place} ${props.width}`
})

// Compute the fixed panel position off the trigger. Runs on trigger mousedown
// (fires just before HeadlessUI toggles open); after teleport the panel lives
// in <body>, so the root only wraps the trigger — no stray recompute on
// clicks inside the open panel.
const computePortalPosition = () => {
  const el = rootRef.value?.$el ?? rootRef.value
  const rect = el?.getBoundingClientRect?.()

  if (!rect) return

  const gap = 4
  const style = { position: 'fixed', zIndex: 10000 }
  
  if (props.placement.startsWith('top')) { 
    style.bottom = `${window.innerHeight - rect.top + gap}px`
  } else {
    style.top    = `${rect.bottom + gap}px`
  }

  if (props.placement.endsWith('start')) {
    style.left   = `${rect.left}px`
  } else {
    style.right  = `${window.innerWidth - rect.right}px`
  }

  portalStyle.value = style
}
</script>

<template>
  <Menu
    as="div"
    ref="rootRef"
    class="relative inline-flex"
    v-slot="{ open }"
    @mousedown="portal && computePortalPosition()"
  >
    <!-- Trigger — any element, commonly AppButton -->
    <MenuButton as="template">
      <slot name="trigger" :open="open" />
    </MenuButton>

    <Teleport to="body" :disabled="!portal">
      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <MenuItems :class="panelClass" :style="portal ? portalStyle : undefined">
          <slot />
        </MenuItems>
      </Transition>
    </Teleport>
  </Menu>
</template>
